import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { lookupCode } from "../lib/cache_api";
import { useOnline } from "../lib/online";
import { enqueueSale, enqueueAdjust, processQueue } from "../lib/sync";
import { useBilling, parseTierLimitError } from "../lib/billing";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, Search, Plus, Minus, Calendar, Receipt, CreditCard, Lock, Trash2, X } from "lucide-react";
import { fmtInt, fmtMoneyFull } from "../lib/format";
import { toast } from "sonner";

export default function Scanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get("event_id") || "";
  const [eventName, setEventName] = useState("");
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5Ref = useRef(null);
  const { online, refreshQueueCount } = useOnline();

  // Cart + Square intent are persisted in localStorage so they survive
  // navigation to Square POS and back. Keyed per-event so different events
  // keep separate carts (important for switching booths mid-day).
  const cartKey = `vv.cart:${eventId || "default"}`;
  const intentKey = `vv.squareIntent:${eventId || "default"}`;
  const [cart, setCart] = useState(() => {
    try { const v = localStorage.getItem(cartKey); return v ? JSON.parse(v) : []; }
    catch { return []; }
  });
  const [squareIntent, setSquareIntent] = useState(() => {
    try { const v = localStorage.getItem(intentKey); return v ? JSON.parse(v) : null; }
    catch { return null; }
  });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [squareConfig, setSquareConfig] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const { billing, openUpgradeModal } = useBilling();
  const integrationsAllowed = !!billing?.limits?.integrations;

  // Persist cart + intent every time they change.
  useEffect(() => {
    try { localStorage.setItem(cartKey, JSON.stringify(cart)); } catch { /* quota etc. */ }
  }, [cart, cartKey]);
  useEffect(() => {
    try {
      if (squareIntent) localStorage.setItem(intentKey, JSON.stringify(squareIntent));
      else localStorage.removeItem(intentKey);
    } catch { /* ignore */ }
  }, [squareIntent, intentKey]);

  useEffect(() => {
    api.get("/square/config").then(({ data }) => setSquareConfig(data)).catch(() => setSquareConfig(null));
  }, []);

  // Square POS callback redirects back to /scanner?paid=<intent_id>. Pick that
  // up on mount, mark the intent as "verifying" so the polling loop re-engages,
  // and clean the URL bar.
  useEffect(() => {
    const paidIntent = searchParams.get("paid");
    if (!paidIntent) return;
    setSquareIntent((cur) => {
      if (cur && cur.intent_id === paidIntent) return { ...cur, status: cur.status || "verifying" };
      return { intent_id: paidIntent, status: "verifying" };
    });
    // Strip the ?paid= from the URL so a refresh doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete("paid");
    setSearchParams(next, { replace: true });
  }, []); // run once on mount

  // Poll the active intent until it completes or fails (max 5 minutes).
  useEffect(() => {
    if (!squareIntent?.intent_id || ["paid", "failed", "no_app"].includes(squareIntent.status)) return;
    let stop = false;
    let polls = 0;
    const tick = async () => {
      if (stop) return;
      polls += 1;
      try {
        const { data } = await api.get(`/square/pos/intent/${squareIntent.intent_id}`);
        setSquareIntent((cur) => ({ ...cur, ...data }));
        if (data.status === "paid") {
          toast.success(`Payment confirmed · ${fmtMoneyFull((data.total_cents || 0) / 100)}`);
          setCart([]);
          return;
        }
        if (data.status === "failed") {
          toast.error(data.verification?.error || data.error_description || "Square payment failed");
          return;
        }
      } catch { /* keep polling */ }
      if (polls < 100) setTimeout(tick, 3000);
    };
    setTimeout(tick, 1500);
    return () => { stop = true; };
  }, [squareIntent?.intent_id, squareIntent?.status]);

  // If the Square POS app fails to open (deeplink didn't trigger), surface a
  // helpful message after 8s so the user isn't stranded on "Waiting for callback…".
  // We track whether the tab was ever backgrounded — if it was, Square POS
  // almost certainly opened, so don't flip to "no_app".
  useEffect(() => {
    if (squareIntent?.status !== "starting") return;
    let tabBackgrounded = false;
    const onVis = () => { if (document.hidden) tabBackgrounded = true; };
    document.addEventListener("visibilitychange", onVis);
    const t = setTimeout(() => {
      setSquareIntent((cur) => {
        if (!cur || cur.status !== "starting") return cur;
        // Tab was backgrounded → Square POS app opened → keep waiting.
        if (tabBackgrounded) return { ...cur, status: "verifying" };
        return { ...cur, status: "no_app" };
      });
    }, 8000);
    return () => { document.removeEventListener("visibilitychange", onVis); clearTimeout(t); };
  }, [squareIntent?.status]);

  const removeCartItem = async (sale_id) => {
    setRemovingId(sale_id);
    try {
      await api.delete(`/sales/${sale_id}`);
      setCart((c) => c.filter((line) => line.sale_id !== sale_id));
      toast.success("Removed from cart");
      // Refresh the lookup so the user sees the restocked variant if visible.
      if (result?.variant?.barcode || result?.variant?.sku) {
        lookup(result.variant.barcode || result.variant.sku);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove that item");
    } finally {
      setRemovingId(null);
    }
  };

  const clearCart = async () => {
    if (cart.length === 0) return;
    if (!window.confirm(`Clear ${cart.length} item${cart.length !== 1 ? "s" : ""} from cart? This will reverse the sales (stock will be restocked).`)) return;
    // Best-effort: delete each sale server-side. If any fail, keep them in the
    // cart so the vendor knows something's off.
    const failures = [];
    for (const line of cart) {
      try { await api.delete(`/sales/${line.sale_id}`); }
      catch { failures.push(line.sale_id); }
    }
    setCart((c) => c.filter((line) => failures.includes(line.sale_id)));
    if (failures.length === 0) toast.success("Cart cleared");
    else toast.error(`${failures.length} item(s) couldn't be removed`);
  };

  const addCustomAmount = async (e) => {
    e?.preventDefault();
    const amount = parseFloat(customAmount);
    if (!isFinite(amount) || amount <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    try {
      const { data: saleDoc } = await api.post("/sales/custom", {
        amount,
        label: customLabel.trim() || undefined,
        event_id: eventId || null,
      });
      setCart((c) => [...c, {
        sale_id: saleDoc.sale_id,
        product_name: saleDoc.product_name,
        sku: saleDoc.sku,
        unit_price: saleDoc.unit_price,
        quantity: saleDoc.quantity,
        total: saleDoc.total,
      }]);
      setCustomAmount("");
      setCustomLabel("");
      setCustomOpen(false);
      setCartOpen(true);
      toast.success(`Added ${fmtMoneyFull(amount)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add custom amount");
    }
  };

  const chargeWithSquare = async () => {
    if (!integrationsAllowed) {
      openUpgradeModal({ feature: "Square POS payments", limit_key: "integrations" });
      return;
    }
    if (!squareConfig?.configured) {
      toast.error("Square is not configured on this server yet.");
      return;
    }
    try {
      const { data } = await api.post("/square/pos/init", {
        sale_ids: cart.map((c) => c.sale_id),
        event_id: eventId || null,
      });
      setSquareIntent({ intent_id: data.intent_id, status: "starting", total_cents: data.total_cents });
      // Trigger the Square POS app via deep link. Android needs an Intent URL;
      // iOS uses the square-commerce-v1:// custom scheme.
      const isAndroid = /android/i.test(navigator.userAgent || "");
      window.location.href = isAndroid
        ? (data.deeplink_android || data.deeplink)
        : (data.deeplink_ios || data.deeplink);
    } catch (err) {
      const tierErr = parseTierLimitError(err);
      if (tierErr) {
        openUpgradeModal(tierErr);
        return;
      }
      toast.error(err?.response?.data?.detail || "Couldn't start Square payment");
    }
  };

  useEffect(() => {
    if (!eventId) return;
    api.get("/events").then(({ data }) => {
      const ev = data.find((e) => e.event_id === eventId);
      if (ev) setEventName(ev.name);
    }).catch(()=>{});
  }, [eventId]);

  const lookup = async (code) => {
    try {
      const { data, source } = await lookupCode(code);
      setResult(data);
      toast.success(source === "cache" ? "Match (offline cache)" : "Match found");
    } catch {
      setResult({ not_found: code });
      toast.error("No match");
    }
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const id = "qr-reader";
      if (!html5Ref.current) html5Ref.current = new Html5Qrcode(id);
      await html5Ref.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 260 },
        async (text) => {
          try { await html5Ref.current.stop(); } catch {}
          setScanning(false);
          setManual(text);
          await lookup(text);
        },
        () => {}
      );
    } catch (e) {
      setScanning(false);
      toast.error("Camera unavailable");
    }
  };

  const stopScan = async () => {
    try { await html5Ref.current?.stop(); } catch {}
    setScanning(false);
  };

  useEffect(() => () => { try { html5Ref.current?.stop(); } catch {} }, []);

  const adjust = async (delta) => {
    if (!result?.product || !result?.variant) return;
    const { product, variant } = result;
    if (!online) {
      const { product: updated } = await enqueueAdjust({
        product_id: product.product_id, variant_id: variant.variant_id, delta,
      });
      const newVariant = (updated?.variants || []).find((v) => v.variant_id === variant.variant_id) || variant;
      setResult({ product: updated || product, variant: newVariant });
      toast("Saved offline — will sync");
      refreshQueueCount();
      return;
    }
    try {
      const { data } = await api.post(`/products/${product.product_id}/variants/${variant.variant_id}/adjust`, null, { params: { delta }});
      const updatedVariant = data.variants.find((v) => v.variant_id === variant.variant_id);
      setResult({ product: data, variant: updatedVariant });
      toast.success(`Stock: ${updatedVariant.stock}`);
    } catch (e) {
      // network failed mid-action — fall back to queue
      const { product: updated } = await enqueueAdjust({
        product_id: product.product_id, variant_id: variant.variant_id, delta,
      });
      const newVariant = (updated?.variants || []).find((v) => v.variant_id === variant.variant_id) || variant;
      setResult({ product: updated || product, variant: newVariant });
      toast("Saved offline — will sync");
      refreshQueueCount();
    }
  };

  const logSale = async () => {
    if (!result?.product || !result?.variant) return;
    const payload = {
      event_id: eventId || null,
      product_id: result.product.product_id,
      variant_id: result.variant.variant_id,
      quantity: 1,
    };
    if (!online) {
      const { product: updated } = await enqueueSale(payload);
      const newVariant = (updated?.variants || []).find((v) => v.variant_id === result.variant.variant_id) || result.variant;
      setResult({ product: updated || result.product, variant: newVariant });
      toast("Saved offline — will sync");
      refreshQueueCount();
      return;
    }
    try {
      const { data: saleDoc } = await api.post("/sales", payload);
      // saleDoc is the Sale row; capture into the receipt cart.
      if (saleDoc?.sale_id) {
        setCart((c) => [...c, {
          sale_id: saleDoc.sale_id,
          product_name: saleDoc.product_name,
          sku: saleDoc.sku,
          unit_price: saleDoc.unit_price,
          quantity: saleDoc.quantity,
          total: saleDoc.total,
        }]);
      }
      toast.success(eventId ? `Sale logged at ${eventName || "event"}` : "Sale logged");
      await lookup(result.variant.barcode || result.variant.sku);
    } catch {
      // fallback: queue
      const { product: updated } = await enqueueSale(payload);
      const newVariant = (updated?.variants || []).find((v) => v.variant_id === result.variant.variant_id) || result.variant;
      setResult({ product: updated || result.product, variant: newVariant });
      toast("Saved offline — will sync");
      refreshQueueCount();
    }
  };

  // Auto-process queue when we come back online
  useEffect(() => {
    if (online) processQueue().then(() => refreshQueueCount());
  }, [online, refreshQueueCount]);

  return (
    <div className="space-y-6" data-testid="scanner-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Field Ops</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Scanner</h1>
      </div>

      {eventId && eventName && (
        <div className="alert-bar !bg-[#111] flex items-center gap-3" data-testid="scanner-event-banner">
          <Calendar size={16}/>
          <span>Selling at: <strong>{eventName}</strong></span>
          <Link to={`/events/${eventId}`} className="brutal-badge !bg-white !text-[#111] ml-auto">Open Event</Link>
        </div>
      )}

      {/* Cart of sales pending receipt */}
      {(cart.length > 0 || cartOpen) && (
        <div className="brutal-card !bg-[#FFB100]" data-testid="receipt-cart">
          <div className="p-4 flex items-center gap-2 flex-wrap">
            <Receipt size={18}/>
            <div className="font-mono text-xs uppercase tracking-widest flex-1 min-w-[120px]">
              {cart.length} {cart.length === 1 ? "item" : "items"} · {fmtMoneyFull(cart.reduce((a,c)=>a+Number(c.total||0),0))}
            </div>
            <button
              onClick={()=>setCartOpen(o=>!o)}
              className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
              data-testid="cart-toggle-btn"
              aria-expanded={cartOpen}
            >{cartOpen ? "Hide" : "Show"}</button>
            {cart.length > 0 && (
              <button onClick={clearCart} className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs" data-testid="receipt-clear-btn">Clear</button>
            )}
            {squareConfig?.configured && cart.length > 0 && (
              <button
                onClick={chargeWithSquare}
                className="brutal-btn !py-1 !px-3 text-xs !bg-black !text-white"
                disabled={!!squareIntent && !["paid", "failed", "no_app"].includes(squareIntent.status)}
                title={integrationsAllowed ? "Charge cart total via Square POS" : "Square POS — Pro plan required"}
                data-testid="square-charge-btn"
              >
                {!integrationsAllowed && <Lock size={11} className="inline mr-1"/>}
                <CreditCard size={12} className="inline mr-1"/> Charge with Square
              </button>
            )}
            {cart.length > 0 && (
              <button onClick={()=>setReceiptOpen(true)} className="brutal-btn !py-1 !px-3 text-xs" data-testid="receipt-open-btn">
                <Receipt size={12} className="inline mr-1"/> Receipt
              </button>
            )}
          </div>
          {cartOpen && (
            <div className="border-t-2 border-black bg-[#FFF5D6] p-3 space-y-2" data-testid="cart-items">
              {cart.length === 0 ? (
                <div className="font-mono text-xs text-[#4A4A4A] text-center py-2">Cart is empty — scan an item or add a custom amount.</div>
              ) : cart.map((line) => (
                <div key={line.sale_id} className="bg-white border-2 border-black p-2 flex items-center gap-2" data-testid={`cart-item-${line.sale_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs font-bold truncate">{line.product_name}</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A] truncate">
                      {line.sku ? `${line.sku} · ` : ""}{line.quantity} × {fmtMoneyFull(line.unit_price)}
                    </div>
                  </div>
                  <div className="font-mono text-xs font-bold whitespace-nowrap">{fmtMoneyFull(line.total)}</div>
                  <button
                    onClick={() => removeCartItem(line.sale_id)}
                    disabled={removingId === line.sale_id}
                    className="brutal-btn brutal-btn-secondary !p-1 !min-w-[32px] !min-h-[32px] disabled:opacity-30"
                    aria-label={`Remove ${line.product_name}`}
                    data-testid={`cart-remove-${line.sale_id}`}
                  ><Trash2 size={12}/></button>
                </div>
              ))}
              <button
                onClick={() => setCustomOpen(true)}
                className="brutal-btn brutal-btn-secondary w-full !py-2 text-xs"
                data-testid="cart-custom-amount-btn"
              >
                <Plus size={12} className="inline mr-1"/> Add custom amount
              </button>
            </div>
          )}
        </div>
      )}

      {/* Always-visible "Add custom amount" entry point when cart is empty — keeps
          the booth flow one tap away even before scanning anything. */}
      {cart.length === 0 && !cartOpen && (
        <button
          onClick={() => { setCartOpen(true); setCustomOpen(true); }}
          className="brutal-btn brutal-btn-secondary text-xs"
          data-testid="open-custom-amount-btn"
        >
          <Plus size={12} className="inline mr-1"/> Add custom amount
        </button>
      )}

      {squareIntent && !["paid", "failed"].includes(squareIntent.status) && (
        <div className={`alert-bar ${squareIntent.status === "no_app" ? "!bg-[#C53030]" : "!bg-[#111]"} !text-white flex items-center gap-3 flex-wrap`} data-testid="square-status">
          <CreditCard size={16}/>
          {squareIntent.status === "no_app" ? (
            <>
              <span className="font-mono text-xs flex-1">
                Square POS app didn't open. Install it, or try again.
              </span>
              <a
                href={/iPhone|iPad|iPod/i.test(navigator.userAgent) ? "https://apps.apple.com/app/square-point-of-sale-pos/id335393788" : "https://play.google.com/store/apps/details?id=com.squareup"}
                target="_blank"
                rel="noreferrer"
                className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
                data-testid="square-install-link"
              >Get Square POS</a>
              <button onClick={()=>setSquareIntent(null)} className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs" data-testid="square-status-cancel">Dismiss</button>
            </>
          ) : (
            <>
              <span className="font-mono text-xs flex-1">
                Square: <strong className="uppercase">{squareIntent.status || "starting"}</strong> · {fmtMoneyFull((squareIntent.total_cents || 0) / 100)} · waiting for callback…
              </span>
              <button onClick={()=>setSquareIntent(null)} className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs">Cancel</button>
            </>
          )}
        </div>
      )}

      {receiptOpen && (
        <ReceiptModal
          cart={cart}
          eventId={eventId}
          onClose={()=>setReceiptOpen(false)}
          onSent={()=>{ setCart([]); setReceiptOpen(false); }}
        />
      )}

      {customOpen && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center" data-testid="custom-amount-modal">
          <div className="absolute inset-0 bg-black/60" onClick={()=>setCustomOpen(false)} aria-hidden/>
          <form
            onSubmit={addCustomAmount}
            className="relative w-full sm:max-w-md bg-white border-2 border-black shadow-[6px_6px_0_0_#111] animate-slide-up"
          >
            <div className="bg-[#FFB100] border-b-2 border-black px-4 py-3 flex items-center gap-2">
              <Plus size={18}/>
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest">Cart</div>
                <h2 className="font-heading text-base font-black uppercase">Add custom amount</h2>
              </div>
              <button
                type="button"
                onClick={()=>setCustomOpen(false)}
                className="brutal-btn brutal-btn-secondary !p-1 !min-w-[36px] !min-h-[36px]"
                aria-label="Close"
                data-testid="custom-amount-close"
              ><X size={16}/></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="brutal-label">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e)=>setCustomAmount(e.target.value)}
                  className="brutal-input w-full !text-2xl !font-bold !min-h-[56px]"
                  placeholder="0.00"
                  autoFocus
                  data-testid="custom-amount-input"
                />
              </div>
              <div>
                <label className="brutal-label">Description (optional)</label>
                <input
                  type="text"
                  value={customLabel}
                  onChange={(e)=>setCustomLabel(e.target.value)}
                  className="brutal-input w-full"
                  placeholder="e.g. Commission piece"
                  maxLength={120}
                  data-testid="custom-amount-label"
                />
              </div>
              <div className="font-mono text-[10px] text-[#4A4A4A]">
                Logs as a sale at "{customLabel.trim() || "Custom amount"}" — no stock movement. You can charge it via Square just like any other sale.
              </div>
            </div>
            <div className="border-t-2 border-black p-3 flex gap-2">
              <button
                type="button"
                onClick={()=>setCustomOpen(false)}
                className="brutal-btn brutal-btn-secondary flex-1"
              >Cancel</button>
              <button
                type="submit"
                className="brutal-btn flex-1"
                data-testid="custom-amount-submit"
              >Add to cart</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="brutal-card p-6">
          <h2 className="font-heading text-xl font-black uppercase mb-4">Webcam Scan</h2>
          <div id="qr-reader" ref={scannerRef} className="bg-black aspect-video border-2 border-black" />
          <div className="flex gap-2 mt-4">
            {!scanning ? (
              <button className="brutal-btn" onClick={startScan} data-testid="start-scan-btn">
                <Camera size={16}/> Start Camera
              </button>
            ) : (
              <button className="brutal-btn brutal-btn-danger" onClick={stopScan} data-testid="stop-scan-btn">Stop</button>
            )}
          </div>

          <div className="mt-6">
            <label className="brutal-label">Or type / paste code</label>
            <div className="flex gap-2">
              <input className="brutal-input flex-1" value={manual} onChange={(e)=>setManual(e.target.value)} data-testid="manual-code-input" placeholder="Scan or type…"/>
              <button className="brutal-btn" onClick={()=>manual && lookup(manual)} data-testid="manual-lookup-btn">
                <Search size={14}/> Lookup
              </button>
            </div>
          </div>
        </div>

        <div className="brutal-card p-6">
          <h2 className="font-heading text-xl font-black uppercase mb-4">Last Scanned</h2>
          {!result && <div className="font-mono text-sm text-[#4A4A4A]">Nothing yet. Scan or look up a code.</div>}
          {result?.not_found && (
            <div className="alert-bar !bg-[#D9381E]">Not found: {result.not_found}</div>
          )}
          {result?.product && result?.variant && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {result.product.photo_base64 ? (
                  <img src={result.product.photo_base64} alt="" className="w-24 h-24 object-cover border-2 border-black"/>
                ) : (
                  <div className="w-24 h-24 bg-[#EAE7DF] border-2 border-dashed border-black"/>
                )}
                <div>
                  <div className="font-heading text-2xl font-black uppercase">{result.product.name}</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">{result.variant.sku}</div>
                  <div className="font-mono text-xs mt-1">
                    {Object.entries(result.variant.attributes || {}).map(([k,v]) => `${k}: ${v}`).join(" · ") || "Default"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="brutal-btn brutal-btn-secondary !p-3 sm:!p-3 min-w-[44px] min-h-[44px]" onClick={()=>adjust(-1)} data-testid="scanner-minus">
                  <Minus size={22}/>
                </button>
                <div className="flex-1 text-center border-2 border-black p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">Stock</div>
                  <div className="font-mono text-3xl font-bold" data-testid="scanner-stock-value">{fmtInt(result.variant.stock)}</div>
                </div>
                <button className="brutal-btn !p-3 sm:!p-3 min-w-[44px] min-h-[44px]" onClick={()=>adjust(1)} data-testid="scanner-plus">
                  <Plus size={22}/>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button className="brutal-btn brutal-btn-success" onClick={logSale} data-testid="log-sale-btn">Log Sale (x1)</button>
                <Link to={`/products/${result.product.product_id}`} className="brutal-btn brutal-btn-secondary">Open</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Receipt modal — print / email / sms (email + sms mock until creds set)
// =====================================================================
function ReceiptModal({ cart, eventId, onClose, onSent }) {
  const [delivery, setDelivery] = useState("print");
  const [to, setTo] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/receipts/preview", {
          sale_ids: cart.map((c) => c.sale_id),
          event_id: eventId || null,
          buyer_name: buyerName || "",
          delivery: "print",
        });
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [cart, eventId, buyerName]);

  const submit = async () => {
    if (delivery !== "print" && !to.trim()) {
      toast.error("Enter an email");
      return;
    }
    setBusy(true);
    try {
      if (delivery === "print") {
        // Print: open the receipt HTML in a new window.
        const w = window.open("", "", "width=420,height=720");
        if (!w) { toast.error("Pop-up blocked"); return; }
        const doc = w.document;
        doc.open();
        doc.write(preview?.html || "<p>No receipt</p>");
        doc.close();
        w.focus(); w.print();
        toast.success("Receipt printed");
        onSent();
        return;
      }
      const { data } = await api.post("/receipts/send", {
        sale_ids: cart.map((c) => c.sale_id),
        event_id: eventId || null,
        delivery, to: to.trim(), buyer_name: buyerName.trim(),
      });
      const d = data.delivery || {};
      if (d.status === "sent") toast.success(`Receipt ${delivery} sent to ${d.to}`);
      else if (d.status === "mocked") toast(`Mocked ${delivery} send to ${d.to} — wire credentials to enable`, { duration: 5000 });
      else toast.error(`${delivery} delivery failed: ${d.error || "unknown"}`);
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send receipt");
    } finally { setBusy(false); }
  };

  const r = preview?.receipt;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="receipt-modal">
      <div className="brutal-card !bg-white w-full max-w-lg p-6 space-y-4" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Receipt</div>
            <h3 className="font-heading text-2xl font-black uppercase">Send to buyer</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F2EC]">✕</button>
        </div>

        <div className="brutal-card !shadow-none border-2 border-black p-3 bg-[#F4F2EC] max-h-48 overflow-auto">
          {r ? (
            <>
              {r.lines.map((li, i) => (
                <div key={i} className="font-mono text-xs flex items-baseline gap-2">
                  <span>{li.quantity}x</span><span className="flex-1 truncate">{li.product_name}</span>
                  <span>{fmtMoneyFull(li.line_total)}</span>
                </div>
              ))}
              <div className="font-mono text-xs flex justify-between mt-2 pt-2 border-t border-black">
                <span>Subtotal</span><span>{fmtMoneyFull(r.subtotal)}</span>
              </div>
              {r.tax_rate ? (
                <div className="font-mono text-xs flex justify-between">
                  <span>Tax ({r.tax_rate}%)</span><span>{fmtMoneyFull(r.tax_amount)}</span>
                </div>
              ) : null}
              <div className="font-mono text-sm flex justify-between font-bold mt-1">
                <span>TOTAL</span><span>{fmtMoneyFull(r.total)}</span>
              </div>
            </>
          ) : <div className="font-mono text-xs text-[#4A4A4A]">Loading preview…</div>}
        </div>

        <div>
          <label className="brutal-label">Buyer name (optional)</label>
          <input
            value={buyerName} onChange={(e)=>setBuyerName(e.target.value)}
            className="brutal-input w-full" placeholder="Alice"
            data-testid="receipt-buyer-name"
          />
        </div>

        <div>
          <label className="brutal-label">Delivery</label>
          <div className="grid grid-cols-2 gap-2">
            {["print","email"].map((d) => (
              <button
                key={d} type="button"
                onClick={()=>setDelivery(d)}
                className={`brutal-btn !py-2 ${delivery === d ? "" : "brutal-btn-secondary"}`}
                data-testid={`receipt-delivery-${d}`}
              >{d.toUpperCase()}</button>
            ))}
          </div>
        </div>

        {delivery === "email" && (
          <div>
            <label className="brutal-label">Email</label>
            <input
              type="email"
              value={to} onChange={(e)=>setTo(e.target.value)}
              className="brutal-input w-full"
              placeholder="buyer@example.com"
              data-testid="receipt-to-input"
            />
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="brutal-btn brutal-btn-secondary flex-1" disabled={busy}>Cancel</button>
          <button onClick={submit} className="brutal-btn flex-1" disabled={busy || !preview} data-testid="receipt-submit-btn">
            {busy ? "Sending…" : delivery === "print" ? "Print" : `Send ${delivery.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}